import style from './style.css?url'
import featureCallOutStyles from '~/styles/components/feature-call-outs.css?url'
import platformMarchitectureStyles from '~/components/illustration/platform-marchitecture/platform-marchitecture.css?url'
import quoteStyles from '~/components/quote-section/quote-section.css?url'
import sheetPreviewStyles from '~/styles/components/sheet-preview.css?url'
import heroStyles from '~/components-v2/hero/hero.css?url'
import {
  TbBrandSpeedtest,
  TbTrendingUp,
  TbShieldCheck,
  TbBrandHipchat,
  TbGavel,
  TbDirections,
} from 'react-icons/tb'
import { PlatformMarchitecture } from '~/components/illustration/platform-marchitecture'
import {
  GradientChatSmile,
  GradientJoin,
  GradientModelStack,
  GradientSpeedometer,
} from '~/components/illustration/gradient-icons'
import { QuoteSection } from '~/components/quote-section'
import BigQueryIcon from '~/components/illustration/databases/bigquery.svg?react'
import DatabricksIcon from '~/components/illustration/databases/databricks.svg?react'
import MotherDuckIcon from '~/components/illustration/databases/motherduck.svg?react'
import MysqlIcon from '~/components/illustration/databases/mysql.svg?react'
import PostgresIcon from '~/components/illustration/databases/postgres.svg?react'
import RedshiftIcon from '~/components/illustration/databases/redshift.svg?react'
import SnowFlakeIcon from '~/components/illustration/databases/snowflake.svg?react'
import { ConcentricCircles } from '~/components/illustration/patterns/concentric-circles'
import AutoDataModelDiagram from '~/components/illustration/product/auto-data-model-diagram.svg?react'
import PermissionsCircles from '~/components/illustration/product/permission-circles.svg?react'
import UserAttributes from '~/components/illustration/product/user-attributes.svg?react'
import { SheetGridPreview } from '~/components/illustration/business-intelligence'
import { CallToAction } from '~/utils/a-b'
import { Stack } from '~/components-v2'
import { TradeOffsCircles, fragmentLinks } from '~/fragments'
import { LearnMoreSection } from '~/fragments/learn-more-section/learn-more-section'
import {
  CustomersMarquee,
  customersMarqueeLinks,
} from '~/components/shared/customers-marquee'
import { QuoteTheRounds2 } from '~/config/quotes'
import { Link, useLoaderData } from 'react-router'
import { postCollectionLinks } from '~/components-v2/post-collection/post-collection'

export { meta } from '~/utils'
export const links = () => [
  { rel: 'stylesheet', href: style },
  { rel: 'stylesheet', href: heroStyles },
  { rel: 'stylesheet', href: featureCallOutStyles },
  { rel: 'stylesheet', href: platformMarchitectureStyles },
  { rel: 'stylesheet', href: quoteStyles },
  { rel: 'stylesheet', href: sheetPreviewStyles },
  ...customersMarqueeLinks(),
  ...postCollectionLinks(),
  ...fragmentLinks.tradeOffsCircles(),
]

const title = 'The fast and scalable analytics platform'
const subTitle =
  'Analyze, define, and visualize key metrics, and build exceptional customer-facing data products — all on a single platform.'
const description =
  'Omni is the one business intelligence platform that lets you start analyzing data quickly without the upfront cost of modeling. Focus on answering data questions, and Omni will build a data model as you go, allowing for reusability, governance, and performance optimization.'

export const loader = async () => {
  return { description, subTitle, title }
}

export default function () {
  const { subTitle, title } = useLoaderData<typeof loader>()

  return (
    <>
      <header className="hero product product-shot-inline">
        <Stack gap={8} as="hgroup">
          <h1>{title}</h1>
          <h2>{subTitle}</h2>
          <CallToAction />
        </Stack>

        <div className="product-imagery">
          <img
            src="/images/pages/product/workbook-with-changes.webp"
            alt="Product shot of an Omni workbook showing, SQL and a column visualization."
            className="product-shot"
          />
          <img
            src="/images/pages/product/intro-changes.webp"
            alt="Dialog showing 4 changes to the workbook model."
            className="publish-dialog"
          />
        </div>
      </header>
      <CustomersMarquee />
      <section className="feature-list">
        <div className="feature-call-outs grid cols3">
          <div>
            <h4>
              <TbBrandSpeedtest /> Fast
            </h4>
            <p>
              Explore your data immediately and get instant query results with
              analytics run on modern architecture.
            </p>
          </div>

          <div>
            <h4>
              <TbDirections />
              Flexible
            </h4>
            <p>
              Easily communicate findings with beautiful out-of-the-box
              visualizations and customizable components, including links,
              texts, and images.
            </p>
          </div>

          <div>
            <h4>
              <TbGavel /> Governed
            </h4>
            <p>
              Define, standardize, and maintain metrics in a shared data model
              so your team can use them anywhere and always trust the results.
            </p>
          </div>

          <div>
            <h4>
              <TbTrendingUp /> Scalable
            </h4>
            <p>
              Quickly build, maintain, reuse, and iterate on internal and
              external-facing reports without wasting engineering resources or
              paying enterprise prices.
            </p>
          </div>

          <div>
            <h4>
              <TbShieldCheck /> Secure
            </h4>
            <p>
              Meet all industry-leading security and privacy requirements with
              row- and column-level permissions, content controls, user
              attributes, and SSO authentication.
            </p>
          </div>

          <div>
            <h4>
              <TbBrandHipchat /> Supported
            </h4>
            <p>
              Get expert advice and hands-on support from a team with decades of
              experience in data infrastructure, delivery, analysis, and
              monetization.
            </p>
          </div>
        </div>
      </section>
      <section className="wide">
        <hgroup className="centered-section-title">
          <h3>What makes Omni different</h3>
          <p>Analytics without compromises — or an enterprise price tag.</p>
        </hgroup>
        <TradeOffsCircles />
      </section>
      <section>
        <div className="grid cols2 call-out-cards">
          <div>
            <GradientJoin />
            <h4>Best of both worlds</h4>
            <p>
              Our intuitive point-and-click UI enables business users to explore
              data independently. Power users can choose between SQL or familiar{' '}
              <Link to="/calculations">Excel formulas</Link>.
            </p>
          </div>
          <div>
            <GradientModelStack />
            <h4>A data model that doesn't slow you down</h4>
            <p>
              Start <Link to="/data-modeling">exploring data immediately</Link>{' '}
              and build governance as you query. You can also use your existing
              dbt model.
            </p>
          </div>
          <div>
            <GradientSpeedometer />
            <h4>Instant results</h4>
            <p>
              Our{' '}
              <a href="/blog/under-the-hood-of-omnis-intelligent-cache">
                intelligent cache
              </a>{' '}
              uses in-database and in-memory compute to load query results and
              dashboards instantly, so your team never has to wait.
            </p>
          </div>
          <div>
            <GradientChatSmile />
            <h4>Hands-on support</h4>
            <p>
              With Omni, you get more than just great software. Our expert team
              replies in real-time to answer your team's questions and help you
              get the most out of your data.
            </p>
          </div>
        </div>
      </section>
      <section className="wide">
        <hgroup className="centered-section-title">
          <h3>One platform to meet all your analytics needs</h3>
          <p>
            Everyone wants data, but not everyone wants to log into a BI tool to
            get it. The Omni Platform powers internal reporting, operationalizes
            data into workflows, and puts dashboards and visualizations directly
            into your product.
          </p>
        </hgroup>

        <PlatformMarchitecture />
      </section>
      <section className="full-width gradient-section">
        <hgroup className="centered-section-title">
          <h3>How does Omni work?</h3>
        </hgroup>
        <div className="direct-connection">
          <div className="callout-list">
            <div className="callout database-content">
              <div className="callout-text">
                <hgroup>
                  <h4 className="heading-xl">
                    Direct connections to your data
                  </h4>
                  <p>
                    Omni has native integrations with the most common data
                    warehouses, including AWS Redshift, Google BigQuery,
                    Snowflake, Databricks, MySQL, Postgres, and MotherDuck.
                  </p>

                  <p>
                    Our intelligent cache uses in-database and in-memory compute
                    to optimize query performance at every level, deliver
                    instant results, and drill into row-level detail in the
                    database.
                  </p>
                </hgroup>
              </div>
              <div className="callout-illustration">
                <div className="circles">
                  <ConcentricCircles />
                </div>

                <img src="/images/omni-o.svg" alt="" className="omni-o" />

                <div className="icons">
                  <div className="icon">
                    <SnowFlakeIcon />
                  </div>
                  <div className="icon">
                    <BigQueryIcon />
                  </div>
                  <div className="icon">
                    <RedshiftIcon />
                  </div>
                  <div className="icon">
                    <DatabricksIcon />
                  </div>
                  <div className="icon">
                    <MysqlIcon />
                  </div>
                  <div className="icon">
                    <PostgresIcon />
                  </div>
                  <div className="icon">
                    <MotherDuckIcon />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="full-width gradient-section">
        <div className="automated-modeling">
          <div className="callout-list">
            <div className="callout">
              <div className="callout-text">
                <Stack gap={10}>
                  <hgroup>
                    <h4>Automated data modeling</h4>
                    <p>
                      Omni's unique approach to{' '}
                      <Link to="/data-modeling">data modeling</Link> ensures
                      consistency and accuracy while retaining the flexibility
                      for ad hoc exploration. You can start exploring data
                      instantly and update the model as you go.
                    </p>

                    <p>Our three core modeling layers reflect the:</p>

                    <ol>
                      <li>raw database</li>
                      <li>governed data model</li>
                      <li>ad hoc workbook environment</li>
                    </ol>

                    <p>
                      The workbook layer can be used for exploration and one-off
                      queries and to make changes that can be promoted back to
                      the shared model.
                    </p>
                  </hgroup>
                  <hgroup>
                    <h4 className="heading-xl">
                      Bi-directional dbt integration
                    </h4>
                    <p>
                      Omni's dbt{' '}
                      <a href="/blog/three-ways-to-use-our-dbt-integration">
                        integration is bi-directional
                      </a>{' '}
                      to:
                    </p>
                    <ol>
                      <li>seamlessly surface dbt model changes in Omni</li>
                      <li>pull dbt context into Omni for deeper visibility</li>
                      <li>author dbt models from Omni model definitions</li>
                    </ol>
                  </hgroup>
                </Stack>
              </div>
              <div className="callout-illustration left-side-radius">
                <AutoDataModelDiagram />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="full-width gradient-section easy-data-exploration">
        <hgroup className="centered-section-title">
          <h3>Easy data exploration</h3>
        </hgroup>

        <div className="callout-list">
          <div className="callout point-and-click">
            <div className="callout-text">
              <hgroup>
                <h4 className="heading-xl">Point-and-click UI</h4>
                <p>
                  Our UI allows everyone to confidently explore data without
                  writing a single line of code. Users can simply click to
                  create new queries and dashboards, or build on existing ones
                  by adding new filters and fields or making changes to
                  visualizations.
                </p>
              </hgroup>
            </div>
            <div className="callout-illustration">
              <img
                src="/images/pages/home/hero-product-shot.webp"
                alt="An Omni workbook showing a chart"
              />
            </div>
          </div>
          <div className="callout spreadsheet">
            <div className="callout-text">
              <hgroup>
                <h4 className="heading-xl">Spreadsheet formulas</h4>
                <p>
                  Users who prefer working in spreadsheets like Excel can
                  efficiently explore data and create new metrics directly from
                  our workbook UI, no SQL required.{' '}
                  <Link to="/calculations">Omni Calculations</Link> are
                  compatible with the popular spreadsheet formulas many already
                  know and love.
                </p>
              </hgroup>
            </div>
            <div className="callout-illustration">
              <SheetGridPreview />
            </div>
          </div>
          <div className="callout sql-mode">
            <div className="callout-text">
              <hgroup>
                <h4 className="heading-xl">SQL mode</h4>
                <p>
                  When you write SQL in Omni, our{' '}
                  <a href="https://docs.omni.co/analyze-explore/sql">
                    SQL Super Powers
                  </a>{' '}
                  feature automatically parses and restructures your query. Your
                  newly modeled concepts then become accessible in the field
                  picker in the UI so you can efficiently reuse the logic in the
                  future.
                </p>
              </hgroup>
            </div>
            <div className="callout-illustration">
              <div className="clip-box">
                <img src="/images/pages/product/sql-mode.webp" alt="" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <QuoteSection quote={QuoteTheRounds2} />

      <section className="full-width gradient-section flexible-components">
        <hgroup className="centered-section-title">
          <h3>Flexible components</h3>
        </hgroup>

        <div className="callout-list">
          <div className="callout custom-vis">
            <div className="callout-text">
              <hgroup>
                <h4 className="heading-xl">Customizable visualizations</h4>
                <p>
                  Omni has a large library of beautiful out-of-the-box
                  visualizations that you can customize to your liking using our
                  advanced editor, supported by VegaLite. You can also choose
                  from flexible markdown elements to create stylized and dynamic
                  visualizations made up of text, images, charts, and more.
                </p>
              </hgroup>
            </div>
            <div className="callout-illustration">
              <div className="dash-grid">
                <div className="image-card">
                  <img src="/images/pages/product/vis/chart1.webp" alt="" />
                </div>

                <div className="image-card">
                  <img src="/images/pages/product/vis/chart2.webp" alt="" />
                </div>

                <div className="image-card">
                  <img src="/images/pages/product/vis/chart3.webp" alt="" />
                </div>

                <div className="image-card">
                  <img src="/images/pages/product/vis/chart4.webp" alt="" />
                </div>
                <div className="image-card">
                  <img src="/images/pages/product/vis/chart5.webp" alt="" />
                </div>
              </div>
            </div>
          </div>

          <div className="callout iframes">
            <div className="callout-text">
              <hgroup>
                <h4 className="heading-xl">
                  Fast and flexible embedded iframes
                </h4>
                <p>
                  Omni reports and dashboards can be embedded into the internal
                  apps where your team is already working (such as{' '}
                  <a href="/blog/bringing-better-insights-into-your-workflows">
                    Salesforce
                  </a>
                  ) and into your{' '}
                  <a href="/blog/building-usage-analytics">product</a> to create
                  more value for your customers. The embedded dashboards will
                  follow the permissions you set in the app and Omni using SSO,
                  so users will only ever see the data they're supposed to.
                </p>
              </hgroup>
            </div>
            <div className="callout-illustration">
              <img
                src="/images/pages/product/iframe/iframe-notion.webp"
                alt=""
                className="image-card iframe-notion"
              />
              <img
                src="/images/pages/product/iframe/iframe-dash.webp"
                alt=""
                className="image-card iframe-dash"
              />

              <img
                src="/images/pages/product/iframe/iframe-salesforce.webp"
                alt=""
                className="image-card iframe-salesforce"
              />
            </div>
          </div>

          <div className="callout sharing">
            <div className="callout-text">
              <hgroup>
                <h4 className="heading-xl">Easy sharing</h4>
                <p>
                  Securely share content via URL, scheduled reports, or embedded
                  iframes so anyone with permissions can access and act on data
                  within their preferred workflow.
                </p>
              </hgroup>
            </div>
            <div className="callout-illustration">
              <div className="clip-box">
                <img
                  src="/images/pages/internal-analytics/feature-section/communicate/share/delivery-panel.webp"
                  alt=""
                  width="280px"
                  className="image-card"
                />
              </div>

              <div className="clip-box">
                <img
                  src="/images/pages/internal-analytics/feature-section/communicate/share/faux-email-client.webp"
                  alt=""
                  width="300px"
                  className="image-card"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="full-width gradient-section scalable-platform">
        <hgroup className="centered-section-title">
          <h3>Scalable platform administration</h3>
        </hgroup>

        <div className="callout-list">
          <div className="callout permissions">
            <div className="callout-text">
              <hgroup>
                <h4 className="heading-xl">Permissions</h4>
                <p>
                  Organizational administrators can manage{' '}
                  <a href="https://docs.omni.co/administration/users/permissions">
                    user permissions
                  </a>{' '}
                  and data connections to ensure security. Users can be granted
                  varying levels of access at the connection level including no
                  access, viewer, restricted querier, querier, and admin.
                </p>
              </hgroup>
            </div>
            <div className="callout-illustration">
              <PermissionsCircles />
            </div>
          </div>

          <div className="callout user-attributes">
            <div className="callout-text">
              <hgroup>
                <h4 className="heading-xl">User attributes</h4>
                <p>
                  User attributes make it easy to scale user management by
                  mapping user-specific variables to actions such as querying,
                  dashboarding, or general permissions. For example, you can use
                  user attributes to restrict accessible rows of data via Access
                  Filters.
                </p>
              </hgroup>
            </div>
            <div className="callout-illustration">
              <UserAttributes />
            </div>
          </div>
        </div>
      </section>

      <section className="full-width gradient-section expert-support">
        <div className="callout-list">
          <div className="callout expert-support">
            <div className="callout-text">
              <hgroup>
                <h3>Expert data support</h3>
                <p>
                  You're the expert in your business and product, and we're your
                  experts in data. Our team's here to help you with Omni, plus
                  anything else you need to get the most out of your data, —
                  whether that's introductions to partners, advice on building a
                  data-driven culture, or support monetizing your data with
                  in-app reporting.
                </p>
              </hgroup>
            </div>
            <div className="callout-illustration">
              <img
                src="/images/pages/internal-analytics/feature-section/trust/support-avatars.webp"
                alt=""
              />
            </div>
          </div>
        </div>
      </section>

      <LearnMoreSection title="Learn more about Omni" />
    </>
  )
}
